import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { BookOpen, CreditCard, Clock, CheckCircle, AlertCircle, Download, XCircle, ArrowRight, Building2, CalendarDays, Wallet, FileText, MapPin, Home, Eye } from 'lucide-react';
import { formatFeeDisplay } from '../utils/sensitiveDisplay';
import { generateAdmissionReceipt } from '../utils/receiptPdf';
import Dialog from '../components/Dialog';

const admissionProgressSteps = [
  { key: 'applied', label: 'Applied', icon: CheckCircle },
  { key: 'doc_review', label: 'Documents Verified', icon: FileText },
  { key: 'campus_visit', label: 'Campus Visit', icon: MapPin },
  { key: 'confirmed', label: 'Confirmation', icon: Home },
];

const getLatestStatusKey = (admission = {}) => {
  if (['confirmed', 'rejected', 'cancelled'].includes(admission.status)) return admission.status;
  return admission.displayAdmissionStage || admission.admissionStage || admission.status || 'submitted';
};

const getAdmissionStepperSteps = (admission = {}) => {
  const timeline = admission.admissionTimeline || {};
  const isRejected = admission.status === 'rejected' || Boolean(timeline.rejected);
  if (!isRejected) return admissionProgressSteps;
  return [
    ...admissionProgressSteps.filter((step) => step.key !== 'confirmed'),
    { key: 'rejected', label: 'Rejected', icon: XCircle },
  ];
};

const getStepItem = (admission = {}, stepKey) => {
  const timeline = admission.admissionTimeline || {};
  if (stepKey === 'rejected' && !timeline.rejected && admission.status === 'rejected') {
    return { completedAt: admission.updatedAt };
  }
  return timeline[stepKey];
};

export default function AdmissionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feeSettings, setFeeSettings] = useState({
    platformFeeTotal: 0,
  });
  const [cancelAdmission, setCancelAdmission] = useState(null);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [confirmedCancelAdmission, setConfirmedCancelAdmission] = useState(null);

  useEffect(() => {
    fetchAdmissions();
  }, []);

  const fetchAdmissions = async () => {
    setLoading(true);
    try {
      const [admissionsRes, settingsResult] = await Promise.all([
        axios.get('/api/applications/my/admissions'),
        axios.get('/api/settings/public').then((res) => res.data).catch(() => null),
      ]);
      setAdmissions(admissionsRes.data);
      if (settingsResult) {
        setFeeSettings({
          platformFeeTotal: settingsResult.platformFeeTotal ?? 0,
        });
      }
    } catch (err) {
      toast.error('Failed to load admissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isInactiveAdmission = (admission) => ['cancelled', 'rejected'].includes(admission.status);
  const isConfirmedAdmission = (admission) =>
    admission.paymentStatus === 'completed' && !isInactiveAdmission(admission);
  const isAdminConfirmedAdmission = (admission) =>
    admission.paymentStatus === 'completed' && admission.status === 'confirmed';
  const isPaidCancelledAdmission = (admission) =>
    admission.paymentStatus === 'completed' && admission.status === 'cancelled';
  const getAmountDisplayValue = (admission) => {
    if (admission.paymentStatus === 'completed') return admission.totalPaid;

    const existingPaymentTotal = Number(admission.payment?.totalAmount || 0);
    if (existingPaymentTotal > 0) return existingPaymentTotal;

    const baseFee = Number(admission.baseFee ?? admission.fee ?? 0);
    const platformFeeTotal = Number(feeSettings.platformFeeTotal || 0);
    return Number((baseFee + platformFeeTotal).toFixed(2));
  };

  const confirmedAdmissionsCount = admissions.filter(isConfirmedAdmission).length;
  const hasActiveConfirmedAdmission = admissions.some(isConfirmedAdmission);
  const isBlockedByAdmissionLimit = (admission) =>
    hasActiveConfirmedAdmission &&
    !isConfirmedAdmission(admission) &&
    !isInactiveAdmission(admission) &&
    ['pending', 'pay_later', 'failed'].includes(admission.paymentStatus);

  const getStatusBadge = (admission) => {
    if (isBlockedByAdmissionLimit(admission)) {
      return { icon: AlertCircle, label: 'Cancelled', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };
    }

    const latestStatus = getLatestStatusKey(admission);

    if (latestStatus === 'cancelled') {
      return { icon: AlertCircle, label: 'Cancelled', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' };
    }

    if (latestStatus === 'rejected') {
      return { icon: AlertCircle, label: 'Rejected', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    }

    if (latestStatus === 'doc_review_rejected') {
      return { icon: AlertCircle, label: 'Documents Not Verified', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    }

    if (latestStatus === 'document_updated') {
      return { icon: FileText, label: 'Pending Document Update', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' };
    }

    if (latestStatus === 'doc_review') {
      return { icon: CheckCircle, label: 'Documents Verified', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
    }

    if (latestStatus === 'campus_visit') {
      return { icon: CheckCircle, label: 'Campus Visit Completed', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' };
    }

    if (isAdminConfirmedAdmission(admission)) {
      return { icon: CheckCircle, label: 'Admission Confirmed', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
    }

    switch (admission.paymentStatus) {
      case 'completed':
        return { icon: CheckCircle, label: 'Pre Registration Confirmed', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
      case 'pay_later':
        return { icon: Clock, label: 'Pay Later', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' };
      case 'failed':
        return { icon: AlertCircle, label: 'Payment Failed', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
      case 'pending':
      default:
        return { icon: AlertCircle, label: 'Pending Payment', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
    }
  };

  const handlePayNow = (admission) => {
    if (isInactiveAdmission(admission)) {
      toast.error('This application is cancelled and cannot be paid.');
      return;
    }

    if (hasActiveConfirmedAdmission) {
      toast.error('You already have a confirmed admission for this academic year.');
      return;
    }

    navigate(`/payment/${admission.applicationId}`, {
      state: { application: admission }
    });
  };

  const handleDownloadReceipt = async (admission) => {
    if (admission.paymentStatus !== 'completed' && !admission.paymentId) {
      toast.error('Receipt is available after payment confirmation.');
      return;
    }

    const toastId = toast.loading('Generating receipt...');
    try {
      await generateAdmissionReceipt({
        application: admission,
        applicationId: admission.applicationId,
        paymentId: admission.paymentId,
        studentName: admission.fullName || user?.name || 'Student'
      });
      toast.success('Receipt downloaded!', { id: toastId });
    } catch (err) {
      console.error('PDF Error:', err);
      toast.error('Receipt generation failed.', { id: toastId });
    }
  };

  const openCancelModal = (admission) => {
    if (isAdminConfirmedAdmission(admission)) {
      setConfirmedCancelAdmission(admission);
      return;
    }
    setCancelAdmission(admission);
    setCancelConfirmText('');
  };

  const closeCancelModal = () => {
    setCancelAdmission(null);
    setCancelConfirmText('');
  };

  const confirmCancelEnabled = cancelConfirmText.trim().toLowerCase() === 'confirm';

  const formatAdmissionDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  const handleUpdateDocuments = (admission) => {
    if (isInactiveAdmission(admission)) {
      toast.error('This application is closed and documents cannot be updated.');
      return;
    }
    navigate('/profile?section=documents', {
      state: { application: admission },
    });
  };

  const formatAdmissionDateTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-6 pb-28 px-3 sm:px-4 sm:pt-10 sm:pb-32 md:pb-10" style={{ background: 'var(--bg)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-[1.5rem] sm:rounded-[2rem] border p-6 sm:p-10 md:p-14 flex flex-col items-center justify-center gap-3 sm:gap-4 min-h-[280px] sm:min-h-[420px]"
            style={{ background: 'var(--card)', borderColor: 'var(--card-border)', boxShadow: '0 12px 28px rgba(15,23,42,0.04)' }}>
            <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full border-[3px] sm:border-4 border-indigo-500 border-t-transparent animate-spin motion-reduce:animate-none" />
            <div className="text-center">
              <p className="text-lg font-black text-slate-900" style={{ fontFamily: 'Poppins' }}>Loading Admissions</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Fetching your course applications and payment history.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 pb-28 px-3 sm:px-4 sm:pt-10 sm:pb-32 md:pb-10" style={{ background: 'var(--bg)' }}>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 md:mb-10">
          <div className="rounded-[2rem] border overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.1) 0%, rgba(255,255,255,0.98) 48%, rgba(14,165,233,0.08) 100%)', borderColor: 'var(--card-border)', boxShadow: '0 24px 64px rgba(15,23,42,0.06)' }}>
            <div className="px-6 py-7 sm:px-8 sm:py-9 md:px-10 md:py-10">
           
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl">
                  <h1 className="text-3xl sm:text-4xl font-black text-slate-950 mb-3" style={{ fontFamily: 'Poppins' }}>
                    My Admissions
                  </h1>
                  <p className="text-[13px] sm:text-[15px] leading-5 sm:leading-6 max-w-xl" style={{ color: 'var(--text-muted)' }}>
                    Track every application, payment state, and next step from one clean admission dashboard.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:min-w-[280px]">
                  <div className="rounded-[1.1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.8)', borderColor: 'rgba(148,163,184,0.16)' }}>
                    <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-slate-400 mb-1.5 sm:mb-2">Total</p>
                    <p className="text-xl sm:text-2xl font-black text-slate-900 leading-none">{admissions.length}</p>
                    <p className="text-[11px] sm:text-xs mt-1 text-slate-500">Applications</p>
                  </div>
                  <div className="rounded-[1.1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.8)', borderColor: 'rgba(148,163,184,0.16)' }}>
                    <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-slate-400 mb-1.5 sm:mb-2">Confirmed</p>
                    <p className="text-xl sm:text-2xl font-black text-slate-900 leading-none">{confirmedAdmissionsCount}</p>
                    <p className="text-[11px] sm:text-xs mt-1 text-slate-500">Reserved seats</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {admissions.length === 0 ? (
          <div className="rounded-[1.5rem] sm:rounded-[2rem] border p-6 sm:p-12 text-center"
            style={{ background: 'var(--card)', borderColor: 'var(--card-border)', boxShadow: '0 12px 30px rgba(15,23,42,0.04)' }}>
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-[1.25rem] sm:rounded-3xl mx-auto mb-4 sm:mb-5 flex items-center justify-center"
              style={{ background: 'rgba(79,70,229,0.08)', color: 'var(--primary)' }}>
              <BookOpen size={28} />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-3" style={{ fontFamily: 'Poppins' }}>No Applications Yet</h3>
            <p className="text-sm max-w-md mx-auto mb-6 leading-6" style={{ color: 'var(--text-muted)' }}>
              You haven&apos;t applied to any courses yet. Explore available programs and start your first admission journey.
            </p>
            <button
              onClick={() => navigate('/courses')}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 sm:px-6 rounded-xl sm:rounded-2xl font-extrabold text-sm text-white transition-all motion-reduce:transform-none hover:translate-y-[-1px]"
              style={{ background: 'linear-gradient(135deg, var(--primary) 0%, #2563EB 100%)', boxShadow: '0 12px 24px rgba(79,70,229,0.18)' }}
            >
              Browse Courses
              <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div className="rounded-[1.5rem] sm:rounded-[2rem] p-3.5 sm:p-7 md:p-8 border"
            style={{ background: 'var(--card)', borderColor: 'var(--card-border)', boxShadow: '0 8px 18px rgba(15,23,42,0.03)' }}>
            <div className="flex flex-col gap-2.5 sm:gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-7">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-[1rem] sm:rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(79,70,229,0.08)', color: 'var(--primary)' }}>
                  <CreditCard size={18} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Poppins' }}>
                    Admission History
                  </h2>
                  <p className="text-[13px] sm:text-sm mt-0.5 sm:mt-1 leading-5" style={{ color: 'var(--text-muted)' }}>
                    Review payments, confirmations, receipts, and next actions.
                  </p>
                </div>
              </div>
              <div className="inline-flex w-fit self-start sm:self-auto items-center gap-2 px-3 py-2 rounded-full text-xs font-extrabold uppercase tracking-[0.16em]"
                style={{ background: 'rgba(15,23,42,0.04)', color: '#475569' }}>
                <FileText size={13} />
                {admissions.length} Active Record{admissions.length > 1 ? 's' : ''}
              </div>
            </div>

            <div className="grid gap-3 sm:gap-5">
            {admissions.map((admission) => {
              const statusInfo = getStatusBadge(admission);
              const StatusIcon = statusInfo.icon;
              const canPay =
                !hasActiveConfirmedAdmission &&
                !isInactiveAdmission(admission) &&
                (admission.paymentStatus === 'pending' || admission.paymentStatus === 'pay_later' || admission.paymentStatus === 'failed');
              const blockedByLimit = isBlockedByAdmissionLimit(admission);
              const paidCancelled = isPaidCancelledAdmission(admission);
              const isPaid = admission.paymentStatus === 'completed';
              const hasPaymentId = Boolean(admission.paymentId);

              return (
                <div
                  key={admission._id}
                  className="rounded-[1.25rem] sm:rounded-[1.65rem] border p-3.5 sm:p-6 md:p-7 transition-colors duration-200"
                  style={{ background: '#FFFFFF', borderColor: 'rgba(148,163,184,0.14)', boxShadow: '0 4px 10px rgba(15,23,42,0.025)' }}
                >
                  <div className="flex flex-col gap-3.5 sm:gap-6">
                    <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <h3 className="font-black text-lg sm:text-2xl text-slate-950 leading-snug break-words">{admission.courseName}</h3>
                        <p className="text-[13px] sm:text-base font-semibold mt-1 sm:mt-1.5 leading-5 sm:leading-6" style={{ color: 'var(--text-muted)' }}>
                          {admission.programName}
                        </p>
                      </div>

                      <div
                        className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full text-[11px] sm:text-sm font-extrabold border w-fit max-w-full ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                      >
                        <StatusIcon size={15} className="shrink-0" />
                        <span className="truncate sm:whitespace-nowrap">{statusInfo.label}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2.5 sm:grid sm:grid-cols-2 xl:grid-cols-3 sm:gap-3.5">
                      <div className="rounded-[1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ borderColor: 'rgba(148,163,184,0.12)', background: '#F8FAFC' }}>
                        <div className="flex items-center gap-2 text-slate-400 mb-1.5 sm:mb-2">
                          <Building2 size={14} />
                          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em]">Institution</p>
                        </div>
                        <p className="text-[13px] sm:text-sm font-bold text-slate-900 leading-5 sm:leading-6 break-words">{admission.collegeName || 'SNS Institutions'}</p>
                      </div>
                      <div className="rounded-[1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ borderColor: 'rgba(148,163,184,0.12)', background: '#F8FAFC' }}>
                        <div className="flex items-center gap-2 text-slate-400 mb-1.5 sm:mb-2">
                          <FileText size={14} />
                          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em]">Application ID</p>
                        </div>
                        <p className="text-[13px] sm:text-sm font-black text-slate-900 break-all leading-5">{admission.applicationId}</p>
                      </div>
                      <div className="rounded-[1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ borderColor: 'rgba(148,163,184,0.12)', background: '#F8FAFC' }}>
                        <div className="flex items-center gap-2 text-slate-400 mb-1.5 sm:mb-2">
                          <Wallet size={14} />
                          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em]">
                            {isPaid ? 'Amount Paid' : 'Amount To Be Paid'}
                          </p>
                        </div>
                        <p className="text-sm font-black text-slate-900">{formatFeeDisplay(getAmountDisplayValue(admission))}</p>
                      </div>
                      {hasPaymentId && (
                        <div className="rounded-[1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ borderColor: 'rgba(148,163,184,0.12)', background: '#F8FAFC' }}>
                          <div className="flex items-center gap-2 text-slate-400 mb-1.5 sm:mb-2">
                            <CreditCard size={14} />
                            <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em]">Payment ID</p>
                          </div>
                          <p className="text-[13px] sm:text-sm font-bold text-slate-900 break-all leading-5">{admission.paymentId}</p>
                        </div>
                      )}
                      <div className="rounded-[1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ borderColor: 'rgba(148,163,184,0.12)', background: '#F8FAFC' }}>
                        <div className="flex items-center gap-2 text-slate-400 mb-1.5 sm:mb-2">
                          <CalendarDays size={14} />
                          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em]">Date</p>
                        </div>
                        <p className="text-[13px] sm:text-sm font-bold text-slate-900 leading-5">{formatAdmissionDate(admission.createdAt)}</p>
                      </div>
                      <div className="rounded-[1rem] sm:rounded-2xl border p-3 sm:p-4" style={{ borderColor: 'rgba(148,163,184,0.12)', background: '#F8FAFC' }}>
                        <div className="flex items-center gap-2 text-slate-400 mb-1.5 sm:mb-2">
                          <BookOpen size={14} />
                          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.18em]">Academic Year</p>
                        </div>
                        <p className="text-[13px] sm:text-sm font-bold text-slate-900 leading-5">{admission.academicYear || 'N/A'}</p>
                      </div>
                    </div>

                    <div
                      className="rounded-2xl border bg-white p-4 sm:p-5 shadow-sm"
                      style={{ borderColor: '#E5E7EB', boxShadow: '0 12px 32px rgba(15,23,42,0.04)' }}
                    >
                      <div className="relative">
                        {/* Desktop Horizontal Stepper */}
                        <div className="hidden sm:block">
                          <div className="absolute left-[12.5%] right-[12.5%] top-5 border-t border-dashed" style={{ borderColor: '#D1D5DB' }} />
                          <div className={`grid gap-x-4 ${getAdmissionStepperSteps(admission).length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                            {getAdmissionStepperSteps(admission).map((step) => {
                              const item = getStepItem(admission, step.key);
                              const isApplied = step.key === 'applied';
                              const isCompleted =
                                Boolean(item) ||
                                isApplied ||
                                (step.key === 'confirmed' && admission.status === 'confirmed') ||
                                (step.key === 'rejected' && admission.status === 'rejected');
                              const completedAt = item?.completedAt || (isApplied ? admission.createdAt : '');
                              const StepIcon = step.icon;
                              const isRejectedStep = step.key === 'rejected';

                              return (
                                <div key={step.key} className="relative flex flex-col items-center text-center">
                                  <div
                                    className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
                                      isRejectedStep && isCompleted
                                        ? 'border-[#DC2626] bg-[#DC2626] text-white shadow-sm shadow-red-200'
                                        : isCompleted
                                        ? 'border-[#2563EB] bg-[#2563EB] text-white shadow-sm shadow-blue-200'
                                        : 'border-[#D1D5DB] bg-white text-[#9CA3AF]'
                                    }`}
                                  >
                                    {isCompleted && !isRejectedStep ? <CheckCircle size={18} /> : <StepIcon size={17} />}
                                  </div>
                                  <p className={`mt-2 text-[12px] font-extrabold leading-4 ${isCompleted ? 'text-slate-950' : 'text-slate-500'}`}>
                                    {step.label}
                                  </p>
                                  <p className="mt-1 min-h-[16px] text-[11px] font-medium leading-4 text-slate-400">
                                    {completedAt ? formatAdmissionDateTime(completedAt) : 'Pending'}
                                  </p>
                                  {isRejectedStep && isCompleted && (
                                    <p className="mt-1 text-[11px] font-extrabold text-red-600">Rejected by the institution</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Mobile Vertical Stepper */}
                        <div className="block sm:hidden space-y-4">
                          {getAdmissionStepperSteps(admission).map((step, idx, steps) => {
                            const item = getStepItem(admission, step.key);
                            const isApplied = step.key === 'applied';
                            const isCompleted =
                              Boolean(item) ||
                              isApplied ||
                              (step.key === 'confirmed' && admission.status === 'confirmed') ||
                              (step.key === 'rejected' && admission.status === 'rejected');
                            const completedAt = item?.completedAt || (isApplied ? admission.createdAt : '');
                            const StepIcon = step.icon;
                            const isRejectedStep = step.key === 'rejected';

	                            return (
	                              <div key={step.key} className="flex gap-4 relative">
	                                {idx < steps.length - 1 && (
	                                  <div className="absolute left-5 top-10 bottom-[-16px] w-0.5 border-l border-dashed border-slate-300" />
	                                )}
	                                <div
	                                  className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all ${
	                                    isRejectedStep && isCompleted
	                                      ? 'border-[#DC2626] bg-[#DC2626] text-white shadow-sm shadow-red-200'
	                                      : isCompleted
	                                      ? 'border-[#2563EB] bg-[#2563EB] text-white shadow-sm shadow-blue-200'
	                                      : 'border-[#D1D5DB] bg-white text-[#9CA3AF]'
	                                  }`}
	                                >
	                                  {isCompleted && !isRejectedStep ? <CheckCircle size={18} /> : <StepIcon size={17} />}
	                                </div>
	                                <div className="flex flex-col justify-center min-w-0 pt-0.5">
	                                  <p className={`text-xs font-extrabold leading-tight ${isCompleted ? 'text-slate-950' : 'text-slate-500'}`}>
	                                    {step.label}
	                                  </p>
	                                  <p className="text-[11px] font-medium leading-normal text-slate-400 mt-0.5">
	                                    {completedAt ? formatAdmissionDateTime(completedAt) : 'Pending'}
	                                  </p>
	                                  {isRejectedStep && isCompleted && (
	                                    <p className="text-[11px] font-extrabold leading-normal text-red-600 mt-0.5">Rejected by the institution</p>
	                                  )}
	                                </div>
	                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {admission.status === 'cancelled' && (
                        <div className="mt-4 flex items-center gap-3 rounded-2xl border px-4 py-3" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#EF4444]">
                            <XCircle size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-extrabold text-[#DC2626]">Application Cancelled</p>
                            <p className="text-xs font-medium text-slate-500">
                              {formatAdmissionDateTime(admission.admissionTimeline?.cancelled?.completedAt || admission.updatedAt)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-row w-full gap-2 sm:gap-3 pt-1">
                      {!isInactiveAdmission(admission) && (
                        <button
                          onClick={() => handleUpdateDocuments(admission)}
                          className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all border border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-50/50 sm:bg-indigo-50 sm:border-indigo-100 sm:text-indigo-700 sm:hover:bg-indigo-100"
                        >
                          <FileText size={16} className="text-indigo-600 sm:text-indigo-700" />
                          <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Update</span>
                          <span className="hidden sm:inline">Update Documents</span>
                        </button>
                      )}
                      {canPay ? (
                        <button
                          onClick={() => handlePayNow(admission)}
                          className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all text-white hover:translate-y-[-1px]"
                          style={{
                            background: 'linear-gradient(135deg, var(--primary) 0%, #2563EB 100%)',
                            boxShadow: '0 12px 24px rgba(79,70,229,0.18)'
                          }}
                        >
                          <CreditCard size={16} className="text-white" />
                          <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Pay Now</span>
                          <span className="hidden sm:inline">Pay Now</span>
                        </button>
                      ) : isConfirmedAdmission(admission) ? (
                        <>
                          <button
                            onClick={() => handleDownloadReceipt(admission)}
                            className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 sm:bg-slate-950 sm:border-slate-950 sm:text-white sm:hover:bg-black"
                          >
                            <Download size={16} className="text-slate-600 sm:text-white" />
                            <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Receipt</span>
                            <span className="hidden sm:inline">Receipt</span>
                          </button>
                          <button
                            onClick={() => navigate(`/confirmation/${admission.applicationId}`, { state: { application: admission } })}
                            className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all border border-emerald-200 bg-white text-emerald-600 hover:bg-emerald-50/50 sm:bg-emerald-50 sm:border-emerald-100 sm:text-emerald-700 sm:hover:bg-emerald-100"
                          >
                            <Eye size={16} className="text-emerald-600 sm:text-emerald-700" />
                            <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Details</span>
                            <span className="hidden sm:inline">View Details</span>
                          </button>
                          <button
                            onClick={() => openCancelModal(admission)}
                            className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all border border-rose-200 bg-white text-rose-600 hover:bg-rose-50/50 sm:bg-rose-50 sm:border-rose-100 sm:text-rose-600 sm:hover:bg-rose-100"
                          >
                            <XCircle size={16} className="text-rose-500 sm:text-rose-600" />
                            <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Cancel</span>
                            <span className="hidden sm:inline">Cancel Admission</span>
                          </button>
                        </>
                      ) : paidCancelled ? (
                        <>
                          <button
                            onClick={() => handleDownloadReceipt(admission)}
                            className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 sm:bg-slate-950 sm:border-slate-950 sm:text-white sm:hover:bg-black"
                          >
                            <Download size={16} className="text-slate-600 sm:text-white" />
                            <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Receipt</span>
                            <span className="hidden sm:inline">Receipt</span>
                          </button>
                          <button
                            onClick={() => navigate(`/cancel-booking/${admission.applicationId}`, { state: { application: admission } })}
                            className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl font-extrabold text-xs sm:text-sm transition-all border border-rose-200 bg-white text-rose-600 hover:bg-rose-50/50 sm:bg-rose-50 sm:border-rose-100 sm:text-rose-600 sm:hover:bg-rose-100"
                          >
                            <Eye size={16} className="text-rose-500 sm:text-rose-600" />
                            <span className="block sm:hidden text-[10px] font-extrabold mt-0.5">Details</span>
                            <span className="hidden sm:inline">View Details</span>
                          </button>
                        </>
                      ) : blockedByLimit ? (
                        <div className="rounded-2xl border px-4 py-3 text-sm font-semibold w-full text-center"
                          style={{ borderColor: 'rgba(148,163,184,0.14)', background: 'rgba(248,250,252,0.8)', color: '#64748B' }}>
                          You have reached the limit for this academic year.
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {cancelAdmission && (
        <div className="mobile-safe-overlay fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-[2px] sm:backdrop-blur-sm animate-fade-in motion-reduce:animate-none">
          <div className="mobile-safe-surface bg-white rounded-[1.4rem] sm:rounded-[1.75rem] p-5 sm:p-8 max-w-md w-full shadow-xl border border-slate-100 animate-scale-up motion-reduce:animate-none">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3 sm:mb-4 text-rose-500">
              <XCircle size={30} />
            </div>
            <h3 className="text-2xl font-black text-center text-slate-950 mb-2" style={{ fontFamily: 'Poppins' }}>Cancel Booking?</h3>
            <p className="text-center text-sm leading-6 text-slate-500 mb-5">
              Type <span className="font-black text-slate-900">confirm</span> to continue. This action is permanent and the paid amount is non-refundable.
            </p>
            <input
              value={cancelConfirmText}
              onChange={(e) => setCancelConfirmText(e.target.value)}
              placeholder="Type confirm"
              className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border border-slate-200 text-sm font-semibold outline-none focus:border-rose-300 focus:ring-4 focus:ring-rose-50 mb-4 sm:mb-5"
              autoFocus
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={closeCancelModal}
                className="flex-1 py-3 rounded-xl sm:rounded-2xl font-extrabold text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                No, Keep it
              </button>
              <button
                disabled={!confirmCancelEnabled}
                onClick={() => navigate(`/cancel-booking/${cancelAdmission.applicationId}`, { state: { application: cancelAdmission } })}
                className={`flex-1 py-3 rounded-xl sm:rounded-2xl font-extrabold text-sm transition-colors ${
                  confirmCancelEnabled
                    ? 'bg-rose-600 text-white hover:bg-rose-700 shadow-md shadow-rose-100'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <Dialog
        isOpen={Boolean(confirmedCancelAdmission)}
        onClose={() => setConfirmedCancelAdmission(null)}
        title="Admission Confirmed"
        description="Your admission was confirmed by the Institution. Please visit the campus to cancel the Admission"
        icon={<div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600"><CheckCircle size={24} /></div>}
      >
        <button
          onClick={() => setConfirmedCancelAdmission(null)}
          className="w-full py-3 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800"
        >
          Got it
        </button>
      </Dialog>
    </div>
  );
}
